import { TextField, TextFieldProps } from "@mui/material";
import { styled } from "@mui/system";
import { SxProps } from "@mui/material";

export type ExtendedTextFieldProps = TextFieldProps & {
  formatNumeric?: boolean;
  borderColor?: string;
  labelSx?: SxProps;
};

const StyledBaseTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "borderColor",
})<{ borderColor?: string }>(({ borderColor, theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "14px",
    "& fieldset": {
      borderColor: borderColor || theme.palette.grey[300],
    },
    "&:hover fieldset": {
      borderColor: borderColor || theme.palette.grey[400],
    },
    "&.Mui-focused fieldset": {
      borderColor: borderColor || theme.palette.primary.main,
    },
  },
  "& .MuiInputBase-input": {
    paddingLeft: "12px",
    backgroundColor: "white",
  },
  "& .MuiInputAdornment-root": {
    marginLeft: 0,
  },
}));

export default function StyledTextField(props: ExtendedTextFieldProps) {
  const { formatNumeric, value, onChange, onKeyDown, ...rest } = props;

  const formatValue = (val: unknown): string => {
    if (typeof val !== "string") return val?.toLocaleString() ?? "";
    const cleaned = val.replace(/,/g, "");
    if (cleaned === "" || cleaned === ".") return cleaned;
    const [intPart, decPart] = cleaned.split(".");
    const intFormatted = Number(intPart).toLocaleString();
    return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formatNumeric) {
      return onChange?.(e);
    }

    const raw = e.target.value
      .replace(/[^0-9.]/g, "") // allow digits + dot
      .replace(/(\..*?)\./g, "$1"); // keep only the first dot

    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: raw,
        name: e.target.name,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  };

  return (
    <StyledBaseTextField
      {...rest}
      value={formatNumeric ? formatValue(value) : value}
      onChange={handleChange}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.key === "Enter") {
          e.preventDefault();
          rest.onSubmit?.(e);
        }
      }}
      slotProps={{
        ...rest.slotProps,
        htmlInput: {
          ...rest.inputProps,
          ...rest.slotProps?.htmlInput,
          ...(formatNumeric && {
            inputMode: "numeric",
            pattern: "[0-9]*",
          }),
        },
      }}
    />
  );
}
